import { observer } from 'mobx-react-lite';
import { useRef, useState } from 'react';
import Button from '@components/core/button/button.tsx';
import { BUTTON_STYLE } from '@components/core/button/button-style-enum.ts';
import Form from '@components/core/form/form.tsx';
import ExperienceExpansionInput from '@components/data/experience-expansion-input/experience-expansion-input.tsx';
import { Experience } from '@/schemas/experience-schema.ts';
import modalStore from '@store/modal-store.ts';
import Icon from '@components/core/icon/icon.tsx';
import { IconEnum } from '@components/core/icon/icon-enum.ts';
import validationService from '@services/validation-service.ts';

interface ExperienceTagDetailsBulkAddProps {
	tagName: string;
	submit: (experienceId: number[]) => unknown;
}

interface ExperienceInput {
	searchTerm: string;
	experience: Experience | undefined;
}

const MAX_INPUTS = 5;

const ExperienceTagDetailsBulk = observer(function ExperienceTagDetailsBulkAdd(
	props: ExperienceTagDetailsBulkAddProps
) {
	const [inputs, setInputs] = useState([
		{ searchTerm: '', experience: undefined as Experience | undefined },
	]);
	const submitButtonRef = useRef<HTMLButtonElement>(null);

	const handleAddInput = () => {
		if (inputs.length < MAX_INPUTS) {
			setInputs([...inputs, { searchTerm: '', experience: undefined }]);
		}
	};

	const handleInputChange = <K extends keyof ExperienceInput>(
		index: number,
		key: K,
		value: ExperienceInput[K]
	) => {
		setInputs((prev) => {
			const updated = [...prev];
			updated[index] = { ...updated[index], [key]: value };
			return updated;
		});
	};

	const isInputValid = (input: (typeof inputs)[number]) =>
		input.experience ??
		(/^\d+$/.test(input.searchTerm) && input.searchTerm.length);

	return (
		<Form
			className={'flex items-center flex-col gap-4 w-full'}
			onSubmit={() => {
				const ids = inputs
					.map((input) =>
						input.experience
							? input.experience.place_id
							: input.searchTerm
					)
					.filter((id) => validationService.databaseId(id));

				props.submit(ids.map((id) => +id));
				modalStore.close();
			}}
		>
			<div
				className={'mb-4'}
			>{`Add experiences to "${props.tagName}".`}</div>

			<div className={'flex flex-col justify-center items-center gap-6'}>
				{inputs.map((input, index) => (
					<div
						className={'focus-within:z-50'}
						key={`add-experience-${index}`}
					>
						<ExperienceExpansionInput
							searchTerm={input.searchTerm}
							setSearchTerm={(val) =>
								handleInputChange(index, 'searchTerm', val)
							}
							experience={input.experience}
							setExperience={(val) =>
								handleInputChange(index, 'experience', val)
							}
							elementToFocus={submitButtonRef}
							focusOnRender={index === 0}
						/>
					</div>
				))}
			</div>

			{inputs.length < MAX_INPUTS && (
				<button
					type={'button'}
					className={
						'flex items-center gap-2 hover:text-blue-300 hover:underline cursor-pointer'
					}
					onClick={handleAddInput}
				>
					<Icon icon={IconEnum.ADD} className={'w-4 h-4'} />
					Add another experience
				</button>
			)}

			<div className={'w-full flex justify-evenly mt-2'}>
				<Button
					title={'Cancel'}
					onClick={() => modalStore.close()}
					type={'reset'}
				/>

				<Button
					title={'Add'}
					buttonStyle={BUTTON_STYLE.PRIMARY}
					disabled={!inputs.some(isInputValid)}
					innerRef={submitButtonRef}
					type={'submit'}
				/>
			</div>
		</Form>
	);
});
export default ExperienceTagDetailsBulk;
